import {
  MetricThresholdColor,
  ThresholdExpression,
} from '@agilelab/plugin-wb-governance-common';
import {
  useDefaultThresholdColors,
  WbButton,
  WbCard,
  WbDivider,
  WbSelect,
  WbTextField,
} from '@agilelab/plugin-wb-platform';
import { configApiRef, useApi } from '@mainstage/core-plugin-api';
import {
  Box,
  Button,
  CardContent,
  FormControl,
  Grid,
  makeStyles,
  MenuItem,
  Theme,
  Typography,
} from '@material-ui/core';
import { FC, useCallback } from 'react';
import { Controller, useFieldArray, UseFormReturn } from 'react-hook-form';
import { MetricMetadataAddForm } from '../../types';
import { MetricNumericFormatCustom } from '../MetricNumericFormatCustom';
import { MetricThresholdSlider } from '../MetricThresholdSlider';

export const fromExpressionToLabel: Record<ThresholdExpression, string> = {
  [ThresholdExpression.Above]: '>',
  [ThresholdExpression.AboveOrEqual]: '>=',
  [ThresholdExpression.Below]: '<',
  [ThresholdExpression.BelowOrEqual]: '<=',
};

const useStyles = makeStyles((theme: Theme) => ({
  formControl: {
    width: '100%',
    margin: theme.spacing(2, 0),
  },
  formRow: {
    display: 'flex',
  },
  description: {
    margin: theme.spacing(0, 0, 2),
  },
  labelItem: {
    '&>div': { width: '100%' },
    [theme.breakpoints.up('sm')]: {
      width: 100 * 2,
    },
    [theme.breakpoints.up('md')]: {
      width: 163 * 2,
    },
  },
  colorItem: {
    '&>div': { width: '100%' },
    [theme.breakpoints.up('sm')]: {
      width: 100,
    },
    [theme.breakpoints.up('md')]: {
      width: 163,
    },
  },
  valueItem: {
    '&>div': { width: '100%' },
    [theme.breakpoints.up('sm')]: {
      width: 100,
    },
    [theme.breakpoints.up('md')]: {
      width: 163,
    },
  },
}));

export const MetricThresholdPicker: FC<
  UseFormReturn<MetricMetadataAddForm>
> = props => {
  const { defaultThresholdColors } = useDefaultThresholdColors();
  const configApi = useApi(configApiRef);

  const colors: MetricThresholdColor[] =
    configApi.getOptional('mesh.governance.metrics.thresholds.colors') ||
    defaultThresholdColors;

  const classes = useStyles();
  const { control } = props;

  const { fields, append, remove } = useFieldArray({
    name: 'thresholds',
    control,
  });

  const updateThresholds = useCallback(
    (value: string, index: number) => {
      const thresholds = props.getValues('thresholds');
      const newThresholds = [...thresholds];
      newThresholds[index].value = value;
      props.setValue('thresholds', newThresholds);
    },
    [props],
  );

  return (
    <FormControl className={classes.formControl}>
      <WbCard title="Thresholds">
        <CardContent>
          <Typography>
            In this section you can define the{' '}
            <span style={{ fontWeight: 600 }}> domain </span>
            of the metric by specification a lower bound and an upper bound. It
            is also possible define the thresholds and labels to associate them
            in the metric evaluation phase
          </Typography>

          <Box style={{ marginTop: 15 }}>
            <MetricThresholdSlider {...props} colors={colors} />

            {fields.length > 0 && (
              <WbDivider
                orientation="horizontal"
                style={{ marginTop: '12px', marginBottom: '18px' }}
              />
            )}
            <Box style={{ display: 'flex', flexDirection: 'column' }}>
              {fields.map((_item, index) => (
                <Grid container key={index} spacing={2} alignItems="center">
                  <Grid item xs={12} sm="auto" className={classes.labelItem}>
                    <Controller
                      control={control}
                      name={`thresholds.${index}.label`}
                      render={({ field, fieldState: { error } }) => (
                        <WbTextField
                          label="Label"
                          id="label"
                          error={Boolean(error)}
                          helperText={error?.message}
                          {...field}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={5} sm="auto" className={classes.colorItem}>
                    <Controller
                      control={control}
                      name={`thresholds.${index}.color`}
                      render={({ field, fieldState: { error } }) => (
                        <WbSelect
                          label="Color"
                          error={Boolean(error)}
                          helperText={error?.message}
                          {...field}
                          onChange={e => {
                            field.onChange(e.target.value);
                            const thresholds = props.getValues('thresholds');
                            const newThresholds = [...thresholds];
                            newThresholds[index].color = e.target.value;
                            props.setValue('thresholds', newThresholds);
                          }}
                        >
                          {colors.map(c => (
                            <MenuItem key={c.label} value={c.label}>
                              {c.label}
                            </MenuItem>
                          ))}
                        </WbSelect>
                      )}
                    />
                  </Grid>

                  <Grid item xs={2} sm="auto">
                    <Typography
                      style={{ fontWeight: 'bold', textAlign: 'center' }}
                    >
                      {fromExpressionToLabel[props.watch('expression')]}
                    </Typography>
                  </Grid>

                  <Grid item xs={5} sm="auto" className={classes.valueItem}>
                    <Controller
                      control={control}
                      name={`thresholds.${index}.value`}
                      render={({ field, fieldState: { error } }) => (
                        <WbTextField
                          label="Value"
                          error={Boolean(error)}
                          helperText={error?.message}
                          {...field}
                          onChange={e => {
                            const newValue = e.target.value;
                            field.onChange(e.target.value);
                            updateThresholds(newValue, index);
                          }}
                          InputProps={{
                            inputComponent: MetricNumericFormatCustom as any,
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} style={{ paddingTop: 8 }}>
                    <WbButton
                      color="primary"
                      variant="outlined"
                      onClick={() => remove(index)}
                      label="Remove"
                    />
                    <WbDivider
                      orientation="horizontal"
                      style={{ marginTop: '12px', marginBottom: '18px' }}
                    />
                  </Grid>
                </Grid>
              ))}
            </Box>

            <Button
              style={{ marginTop: 30 }}
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => {
                append({
                  label: undefined,
                  color: undefined,
                  value: undefined,
                });
              }}
            >
              Add a Threshold
            </Button>
          </Box>
        </CardContent>
      </WbCard>
    </FormControl>
  );
};
